import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, ShieldCheck, Loader2, Sparkles, Lock } from "lucide-react";

const SKYLINE =
  "https://images.unsplash.com/photo-1632860644196-e1e464596ea3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxsb25kb24lMjBmaW5hbmNpYWwlMjBkaXN0cmljdCUyMHNreWxpbmUlMjBkYXJrfGVufDB8fHx8MTc5MDc2NzkwN3ww&ixlib=rb-4.1.0&q=85";

export default function TrueLayerConnectModal({ open, onOpenChange, onConnected }) {
  const { user } = useAuth();
  const isUK = user?.region === "UK";
  const [banks, setBanks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/banking/banks").then(({ data }) => setBanks(data.banks)).catch(() => {});
      setSelected([]);
      setConsent(false);
    }
  }, [open]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const canProceed = isUK && selected.length > 0 && consent;

  const connectDemo = async () => {
    if (!canProceed) return;
    setLoading(true);
    try {
      await api.post("/banking/connect-demo", { bank_ids: selected });
      toast.success(`Linked ${selected.length} UK ${selected.length === 1 ? "account" : "accounts"}`);
      onOpenChange(false);
      onConnected?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const connectTrueLayer = async () => {
    if (!isUK) return;
    setLoading(true);
    try {
      const { data } = await api.get("/banking/truelayer/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14181D] border-white/10 text-white max-w-lg p-0 overflow-hidden gap-0" data-testid="connect-modal">
        <div className="relative h-28">
          <img src={SKYLINE} alt="London skyline" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14181D] to-transparent" />
          <div className="absolute bottom-3 left-5 flex items-center gap-2 text-xs text-[#4DE3BF]">
            <ShieldCheck className="h-4 w-4" /> Secured by TrueLayer · FCA regulated
          </div>
        </div>

        <div className="p-5">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Connect a UK bank</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose your bank to link accounts through Open Banking. Finlo only ever reads your data — it can never move money.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
            {banks.map((b) => {
              const active = selected.includes(b.id);
              return (
                <button
                  key={b.id}
                  data-testid={`bank-option-${b.id}`}
                  disabled={!isUK}
                  onClick={() => toggle(b.id)}
                  className={`relative flex items-center gap-2.5 rounded-xl border p-3 text-left transition disabled:opacity-40 ${
                    active ? "border-[#00C896] bg-[#00C896]/10" : "border-white/10 bg-[#0D0D0D] hover:border-white/25"
                  }`}
                >
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center font-display font-bold text-sm text-white" style={{ background: b.color }}>
                    {b.name[0]}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{b.name}</span>
                    <span className="block text-[10px] text-slate-500 font-num">{b.sortCode}</span>
                  </span>
                  {active && <Check className="h-4 w-4 text-[#00C896] absolute top-2 right-2" />}
                </button>
              );
            })}
          </div>

          <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              data-testid="consent-checkbox"
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              disabled={!isUK}
              className="mt-0.5 border-white/30 data-[state=checked]:bg-[#00C896] data-[state=checked]:border-[#00C896]"
            />
            <span className="text-xs text-slate-400 leading-relaxed">
              I consent to Finlo accessing my account balances and transaction history via TrueLayer's regulated
              Account Information Service, in line with the Payment Services Regulations 2017.
            </span>
          </label>

          {!isUK && (
            <p className="mt-3 text-xs text-amber-300">Bank connectivity is disabled for non-UK users.</p>
          )}

          <div className="mt-5 space-y-2">
            <Button
              data-testid="truelayer-authorise-button"
              onClick={connectTrueLayer}
              disabled={!canProceed || loading}
              className="w-full h-11 rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Authorise with TrueLayer</>}
            </Button>
            <Button
              data-testid="connect-demo-button"
              onClick={connectDemo}
              disabled={!canProceed || loading}
              variant="outline"
              className="w-full h-11 rounded-xl border-[#00C896]/40 bg-transparent text-[#4DE3BF] hover:bg-[#00C896]/10"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Load sandbox demo accounts
            </Button>
          </div>
          <p className="mt-3 text-[10px] text-slate-500 text-center">
            "Authorise with TrueLayer" opens the live sandbox consent flow. "Load sandbox demo accounts" instantly links
            realistic sample UK data so you can explore Finlo.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
